import { record, type Row } from '../ability/context.js';
import { rowAbilityChargeBuckets } from '../ability/charges/charge-entry.js';
import { statusRows } from '../statuses/expiry.js';
import { heldInventoryRows } from '../statuses/gm-boundary.js';
import { movementDecisionView, pinDecisionFacts, finishMovementDecision, decisionRead } from '../movement/decision-view.js';

/** An OOC attempt borrows the held graph. Only written branches get new containers.
 * Mutable turn facts are pinned before yielding; definitions and unrelated floor/compile
 * branches remain shared. This is a forward projection, with no restore/replay journal. */
export function heldRoundFrame(encounter: Row, members: ReadonlyMap<string, Row>): {
  encounter: Row; members: Map<string, Row>; finish: (row: Row) => Row;
} {
  const frame = movementDecisionView(encounter, members);
  const pin = pinDecisionFacts;
  const facts = (row: Row): void => {
    pin(row); pin(row.encounter_creatures); pin(row.encounters); pin(row.inventory); pin(row.position); pin(row.statuses);
    for (const mode of ['target', 'caster'] as const) {
      pin(record(row.statuses)?.[mode]);
      for (const status of statusRows(row, mode)) pin(status);
    }
    const computed = record(row.computed), creature = record(computed?.creature);
    pin(computed); pin(computed?.vitality); pin(computed?._cache); pin(creature);
    for (const key of ['skills', 'speeds', 'modifiers', 'modifier_sources', 'abilities', 'castable']) pin(creature?.[key]);
    pin(row.item_layer_types);
    for (const bucket of rowAbilityChargeBuckets(row)) {
      let at: unknown = row;
      for (const key of bucket.path) { pin(at); at = record(at)?.[key] ?? (Array.isArray(at) ? at[Number(key)] : undefined); }
      pin(at); pin(record(at)?.charges);
    }
  };
  facts(frame.encounter);
  for (const row of frame.members.values()) facts(row);
  // Pin physical row membership/ownership and clocks, without descending item
  // definitions, container contents or unrelated compiled inventory branches.
  pin(frame.encounter.encounter_inventories_arr);
  for (const row of heldInventoryRows(frame).values()) {
    pin(row); pin(row.inventory); pin(row.position); pin(row.statuses);
    for (const mode of ['target', 'caster'] as const) {
      pin(record(row.statuses)?.[mode]); for (const status of statusRows(row, mode)) pin(status);
    }
  }
  let finished = false;
  return { encounter: frame.encounter, members: frame.members, finish: row => {
    if (!finished) { finishMovementDecision(frame); finished = true; }
    return decisionRead(row);
  } };
}
